import { Link } from 'react-router-dom'

// Where the reader has got to, at the top of the curriculum.
//
// The bar is the small part. The Resume link is the point: a 120-lesson course
// with no way back to where you stopped is a folder of documents, and the reader
// has to remember a lesson title to get on with it.

export interface Progress {
  completed: string[]
  total: number
  resumeSlug: string | null
}

export default function CourseProgress({
  progress,
  courseSlug,
}: {
  progress: Progress
  courseSlug: string
}) {
  const done = progress.completed.length
  if (progress.total === 0) return null

  const pct = Math.round((done / progress.total) * 100)
  const finished = progress.resumeSlug === null && done > 0
  // Not started yet, so there is nothing to report and a 0% bar just makes the
  // page look like homework. The Start link on the page already covers this.
  if (done === 0) return null

  return (
    <div className="mb-6 rounded-smooth border border-border-subtle bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-heading text-overline uppercase tracking-[0.14em] text-ink-muted">
          Your progress
        </p>
        <p className="font-body text-body-sm text-ink-soft">
          {done} of {progress.total} lessons{finished ? '' : `, ${pct}%`}
        </p>
      </div>

      <div
        className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brand-navy/10"
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-label="Course progress"
      >
        <div className="h-full rounded-full bg-brand-navy" style={{ width: `${pct}%` }} />
      </div>

      {finished ? (
        <p className="mt-3 font-body text-body-sm text-ink-soft">
          You have finished every lesson in this course.
        </p>
      ) : progress.resumeSlug ? (
        <Link
          to={`/courses/${courseSlug}/${progress.resumeSlug}`}
          className="mt-3 inline-block font-heading text-body-sm font-bold text-brand-navy underline underline-offset-4"
        >
          Pick up where you left off
        </Link>
      ) : null}
    </div>
  )
}
